import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Frame } from '../components/Frame';
import { KrabenMark } from '../components/KrabenMark';
import { CardView } from '../components/CardView';
import { fetchChallenge, submitGuess, type PublicChallenge } from '../lib/challenges';
import { loadProfile } from '../lib/state';
import { getChallenge } from '../data/exercises';
import { track } from '../analytics';
import { StoryPage, StoryPlayback } from '../components/StoryPrint';
import { startPlayer, type Player } from '../lib/story-player';
import type { Storyboard } from '../lib/storyboard';
import { useViewed } from '../lib/viewed';

/** A friend's storyboard of the two of you, with Play it: the reason the link was sent. */
function SentStory({ sb, who }: { sb: Storyboard; who: string }) {
  const [player, setPlayer] = useState<Player | null>(null);
  return (
    <div className="stack" style={{ gap: 10 }}>
      <span className="label">{who}’s story, storyboarded by the Moon</span>
      {sb.title && <h2>{sb.title}</h2>}
      <StoryPage sb={sb} />
      {sb.ending && <div className="sb-end-card">{sb.ending.quote}</div>}
      <button className="btn butter" onClick={() => { track('storyboard_played', { narrated: !!sb.narration, from: 'challenge' }); setPlayer(startPlayer(sb)); }}>▶ Play it</button>
      {player && <StoryPlayback sb={sb} player={player} onClose={() => setPlayer(null)} />}
    </div>
  );
}

/**
 * A friend's challenge (10 §6): the same exercise with the same deal, their
 * work above it. One button. When you finish, both posts sit side by side
 * here and on both feeds, and you are friends. No dare.
 *
 * A guessing game gets one thing more (#96): a box to guess in, before the
 * invitation to make your own. The card has always said "the answer shows
 * after you guess", and until now nothing could keep that — the answer was
 * stored where nothing read it, and the only possible reply to "guess the
 * film" was to go and make your own twelve emoji. Guessing is not scored.
 */
export function ChallengePage() {
  useViewed('challenge');
  const { code = '' } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState<PublicChallenge | null | undefined>(undefined);
  const [guess, setGuess] = useState('');
  const [verdict, setVerdict] = useState<{ correct: boolean; answer: string } | null>(null);
  const [guessing, setGuessing] = useState(false);
  useEffect(() => { fetchChallenge(code).then((x) => { setC(x); if (x) track('link_opened', { code, challengeId: x.challengeId, completed: x.completed }); }).catch(() => setC(null)); }, [code]);

  if (c === undefined) return <Frame><p className="hint">One moment…</p></Frame>;
  if (c === null) return <Frame><h1>That link has gone.</h1><button className="btn ghost" onClick={() => nav('/')}>Today’s exercise</button></Frame>;
  const ex = getChallenge(c.challengeId);
  const talk = c.challengeId === 'story-about-a-friend';
  const nameKey = ex?.input.nameField;

  return (
    <Frame left={<span className="tag">From {c.fromName}</span>} right={<span className="tag butter">{c.minutes} minutes</span>}>
      <div className="row between">
        <h1>{talk ? `${c.fromName} told a story about the two of you${c.toName ? `, ${c.toName}` : ''}.` : `${c.fromName} did ${c.title}.`}</h1>
        <KrabenMark size={64} />
      </div>
      {c.fromStory && <SentStory sb={c.fromStory} who={c.fromName} />}
      {c.fromPost?.audioUrl && (
        <div className="stack" style={{ gap: 6 }}>
          {c.fromStory && <span className="label">Hear {c.fromName} tell it</span>}
          <audio className="player" controls src={c.fromPost.audioUrl} />
        </div>
      )}
      {c.fromPost && !c.fromStory && <CardView pngUrl={c.fromPost.cardUrl} />}
      {c.hasAnswer && !c.completed && (
        <div className="sheet" style={{ gap: 10 }}>
          {verdict ? (
            <>
              <span className="label" style={{ color: verdict.correct ? 'var(--green-dark)' : 'var(--coral-dark)' }}>{verdict.correct ? 'You got it' : 'Not this time'}</span>
              <b style={{ fontSize: 22 }}>{verdict.answer}</b>
              <p className="why">{verdict.correct ? `${c.fromName} will be pleased.` : `You said “${guess}”. ${c.fromName} will enjoy that.`}</p>
            </>
          ) : (
            <>
              <span className="label">Guess it</span>
              <p className="why">{c.fromName} told a whole film in emoji. The answer shows as soon as you say one.</p>
              <input className="input" value={guess} onChange={(e) => setGuess(e.target.value)} placeholder="Which film?" onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
              <button
                className="btn butter"
                disabled={!guess.trim() || guessing}
                onClick={async () => {
                  setGuessing(true);
                  try {
                    const v = await submitGuess(code, guess.trim(), loadProfile().name || undefined);
                    setVerdict(v);
                    track('guess_made', { code, correct: v.correct });
                  } catch { /* the button stays, they can try again */ }
                  finally { setGuessing(false); }
                }}
              >{guessing ? 'One moment' : 'That’s my guess'}</button>
            </>
          )}
        </div>
      )}
      {c.completed && c.toPost ? (
        <>
          <p className="why"><b>{c.toName || 'They'}</b> told one back. Side by side, on both feeds.</p>
          {c.toStory && <SentStory sb={c.toStory} who={c.toName || 'Your friend'} />}
          {c.toPost.audioUrl && <audio className="player" controls src={c.toPost.audioUrl} />}
          {!c.toStory && <CardView pngUrl={c.toPost.cardUrl} />}
          <button className="btn ghost" onClick={() => nav('/')}>Today’s exercise</button>
        </>
      ) : (
        <div className="stack" style={{ marginTop: 'auto' }}>
          <p className="why"><b>Your turn.</b> {talk ? `Tell it from your side: a story about you and ${c.fromName}. ${c.minutes} minutes, out loud. Stories come easier with two people. No sign-up.` : `Same prompt. ${c.minutes} minutes. No sign-up.`}</p>
          {/* an exercise about a named person turns round: the friend's turn is about the sender, so the sender's name goes where theirs was */}
          <button className="btn coral" onClick={() => { track('try_started', { code }); nav(`/do/${c.challengeId}`, { state: { door: 'side', deal: nameKey ? { ...(talk ? {} : c.deal), [nameKey]: c.fromName } : c.deal, challengeCode: code, fromName: c.fromName, myName: c.toName } }); }}>Begin</button>
          <p className="hint" style={{ textAlign: 'center' }}>When you finish, you two are friends here and see each other’s things.</p>
        </div>
      )}
    </Frame>
  );
}
